import { cn } from "@/ds/utils";
import type { DataViewItemBaseProps } from "@/features/common/components/dataview/DataViewItemBaseProps";
import { Spinner } from "@/ds/shadcn/components/ui/spinner";
import { Button } from "@/ds/shadcn/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/ds/shadcn/components/ui/dropdown-menu";
import { AlertCircleIcon, EllipsisVerticalIcon } from "lucide-react";
import { Children, type FC, isValidElement, type PropsWithChildren, type ReactNode } from "react";
import { Link } from "react-router";

export interface GalleryItemProps extends PropsWithChildren, DataViewItemBaseProps {
	title: string;
	description?: string;
	imageUrl: string;
	imageLink?: string;
	isProductImage?: boolean;
	className?: string;
	onClick?: () => void;
}

export const GalleryItem: FC<GalleryItemProps> = props => {
	const {
		title,
		description,
		imageUrl,
		imageLink,
		isProductImage = true,
		className,
		onClick,
		children,
		isLoading,
		error
	} = props;

	let overlayChild: ReactNode = null;
	let menuChild: ReactNode = null;
	const remainingChildren: ReactNode[] = [];

	Children.forEach(children, child => {
		if (isValidElement(child) && child.type === GalleryItemOverlay) {
			if (overlayChild !== null) {
				throw new Error("GalleryItem accepts at most one GalleryItemOverlay child.");
			}

			overlayChild = child;
		} else if (isValidElement(child) && child.type === GalleryItemMenu) {
			if (menuChild !== null) {
				throw new Error("GalleryItem accepts at most one GalleryItemMenu child.");
			}

			menuChild = child;
		} else {
			remainingChildren.push(child);
		}
	});

	const errorOverlay = error && (
		<div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-1 bg-background/50 p-2 text-destructive">
			<AlertCircleIcon className="h-5 w-5 shrink-0" />
			<div className="text-xs text-center line-clamp-3">
				{
					typeof error === "string"
						? error
						: error.problem.description
				}
			</div>
		</div>
	);

	const productImage = isProductImage ? (
		<div className={cn("relative aspect-400/558 w-full bg-black overflow-hidden", className)}>
			<div style={{ backgroundImage: `url(${imageUrl})` }} className="w-full rounded-[6%] h-full bg-background bg-cover bg-center bg-no-repeat inset-shadow-sm transition-transform duration-200 group-hover:scale-[1.03]">
				{overlayChild}
				{errorOverlay}
			</div>
		</div>
	) : (
		<div className={cn("relative aspect-400/558 w-full overflow-hidden", className)}>
			<div style={{ backgroundImage: `url(${imageUrl})` }} className="absolute inset-0 bg-background bg-cover bg-center bg-no-repeat inset-shadow-sm transition-transform duration-200 group-hover:scale-[1.03]">
				{overlayChild}
				{errorOverlay}
			</div>
		</div>
	);

	return (
		<div onClick={onClick} className={cn("flex flex-col relative bg-background border shadow-xs rounded-md overflow-hidden", isLoading && "pointer-events-none", onClick && "group cursor-pointer")}>
			{
				isLoading && (
					<div className="absolute inset-0 z-30 flex items-center justify-center bg-background/50 rounded">
						<Spinner className="h-6 w-6" />
					</div>
				)
			}

			<div className="p-2 flex items-start gap-1">
				<div className="flex-1 min-w-0">
					<div className="type-item truncate">{title}</div>

					{
						description &&
						<div className="type-meta truncate">{description}</div>
					}
				</div>

				{
					menuChild && (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="ghost" size="icon" className="h-6 w-6 shrink-0 -mt-0.5 -mr-0.5">
									<EllipsisVerticalIcon className="h-4 w-4" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent>
								{menuChild}
							</DropdownMenuContent>
						</DropdownMenu>
					)
				}
			</div>

			{
				imageLink ? (
					<Link to={imageLink}>
						{productImage}
					</Link>
				) : productImage
			}

			{
				remainingChildren.length > 0 &&
				<div className="flex flex-col border-t">
					{remainingChildren}
				</div>
			}
		</div>
	);
};

export interface GalleryItemPropertyProps extends PropsWithChildren {
	label?: string;
}

export const GalleryItemProperty: FC<GalleryItemPropertyProps> = props => {
	const {
		label,
		children
	} = props;

	return (
		<div className="flex items-center justify-between gap-3 text-xs px-2 py-1 even:bg-muted/50 dark:even:bg-muted/10">
			{
				label &&
				<div className="type-meta">{label}</div>
			}

			{children}
		</div>
	);
};

export interface GalleryItemOverlayProps extends PropsWithChildren {
	className?: string;
}

export const GalleryItemOverlay: FC<GalleryItemOverlayProps> = props => {
	const {
		className,
		children
	} = props;

	return (
		<div className={cn("absolute top-0 left-0 w-full h-full", className)}>
			{children}
		</div>
	);
};

export const GalleryItemMenu: FC<PropsWithChildren> = (props: PropsWithChildren) => props.children;
